// components/Layout/AppShell.jsx
import React, { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { Avatar, Badge, Button, Menu, MenuButton, MenuItem, MenuList, MenuPopover, MenuTrigger, Text, Tooltip } from "@fluentui/react-components";
import {
  Home24Regular, DataBarVertical24Regular, DocumentAdd24Regular,
  ArrowUpload24Regular, DocumentBulletList24Regular, SignOut24Regular,
  Person24Regular, Settings24Regular, ChevronLeft24Regular, ChevronRight24Regular,
  Organization24Regular, TaskListSquareLtr24Regular, ShieldLock24Regular,
  BuildingMultiple24Regular, CalendarLtr24Regular, Flag24Regular,
  Money24Regular, ArrowTrendingLines24Regular,
} from "@fluentui/react-icons";
import { NavLink, Outlet } from "react-router-dom";
import { useUserAccess } from "../../context/UserAccessContext";
import TrainingNotification from "../Training/TrainingNotification";

function NavItem({ to, label, Icon, highlight, badge, end, collapsed }) {
  return (
    <NavLink
      to={to}
      end={end}
      style={({ isActive }) => ({
        display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", marginBottom: 4,
        borderRadius: 8, textDecoration: "none",
        color: isActive ? "#fff" : highlight ? "rgba(255,220,100,0.9)" : "rgba(255,255,255,0.65)",
        background: isActive ? "rgba(255,255,255,0.15)" : highlight && !isActive ? "rgba(255,220,100,0.07)" : "transparent",
        fontWeight: isActive ? 600 : 400, fontSize: 14, transition: "all 0.15s",
        justifyContent: collapsed ? "center" : "flex-start",
        border: highlight && !isActive ? "1px solid rgba(255,220,100,0.2)" : "1px solid transparent",
      })}
    >
      {({ isActive }) => (
        <Tooltip content={collapsed ? label : ""} relationship="label" positioning="after">
          <span style={{ display: "flex", alignItems: "center", gap: 12, width: "100%" }}>
            <Icon style={{ flexShrink: 0 }} />
            {!collapsed && (
              <>
                <span style={{ flex: 1 }}>{label}</span>
                {badge && !isActive && (
                  <Badge appearance="tint" style={{ fontSize: 9, background: badge.bg || "rgba(255,220,100,0.2)", color: badge.color || "rgba(255,220,100,0.9)" }}>
                    {badge.text}
                  </Badge>
                )}
              </>
            )}
          </span>
        </Tooltip>
      )}
    </NavLink>
  );
}

function NavSection({ label, collapsed }) {
  if (collapsed) return <div style={{ height: 1, background: "rgba(255,255,255,0.1)", margin: "8px 8px" }} />;
  return <div style={{ padding: "10px 12px 4px", fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: "0.8px" }}>{label}</div>;
}

export default function AppShell({ isDemoMode }) {
  const { instance, accounts } = useMsal();
  const [collapsed, setCollapsed] = useState(false);
  const user = accounts[0] || null;
  const { isAdmin, isManager, isEditor, role, loading: accessLoading } = useUserAccess();

  const roleBadgeColor = { admin: "#7B2D8B", editor: "#B8430A", manager: "#006E6E", staff: "#005A9E", viewer: "#4A4A4A" }[role] || "#555";
  const roleLabel = { admin: "Admin", editor: "Editor", manager: "Manager", staff: "Staff", viewer: "Viewer" }[role] || role;

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#F3F2F1" }}>
      {/* Sidebar */}
      <aside style={{ width: collapsed ? 64 : 240, background: "linear-gradient(180deg,#001A3D 0%,#002D7A 100%)", display: "flex", flexDirection: "column", transition: "width 0.2s ease", flexShrink: 0, position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 100, boxShadow: "2px 0 12px rgba(0,0,0,0.18)" }}>
        {/* Logo */}
        <div style={{ padding: collapsed ? "18px 0" : "18px 16px", borderBottom: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", gap: 10, justifyContent: collapsed ? "center" : "flex-start" }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: "linear-gradient(135deg,#0066FF,#00C4CC)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontWeight: 700, fontSize: 14, color: "#fff", letterSpacing: -0.5 }}>LO</div>
          {!collapsed && (
            <div>
              <Text style={{ color: "#fff", fontWeight: 700, fontSize: 14, display: "block" }}>LekOil</Text>
              <Text style={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}>SCM Dashboard</Text>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: "12px 8px", overflowY: "auto" }}>
          {/* Main */}
          <NavSection label="MAIN" collapsed={collapsed} />
          <NavItem to="/" label="Dashboard" Icon={Home24Regular} end collapsed={collapsed} />
          <NavItem to="/charts" label="Analytics" Icon={DataBarVertical24Regular} collapsed={collapsed} />
          <NavItem to="/reports" label="Reports" Icon={DocumentBulletList24Regular} collapsed={collapsed} />

          {/* Activity Tracker + Training — all roles */}
          <NavSection label="WORK" collapsed={collapsed} />
          <NavItem
            to="/activity-tracker"
            label="Activity Tracker"
            Icon={TaskListSquareLtr24Regular}
            badge={{ text: "NEW", bg: "rgba(0,180,100,0.2)", color: "rgba(100,255,160,0.95)" }}
            collapsed={collapsed}
          />
          {isEditor && (
            <NavItem
              to="/activity-tracker/bulk-upload"
              label="Bulk Upload"
              Icon={ArrowUpload24Regular}
              collapsed={collapsed}
            />
          )}
          <NavItem
            to="/training-roster"
            label="Safety Moment & KSS"
            Icon={CalendarLtr24Regular}
            collapsed={collapsed}
          />
          {(isEditor || isAdmin) && (
            <NavItem
              to="/cfo-issues"
              label="CFO Issue List"
              Icon={Flag24Regular}
              highlight
              badge={{ text: "CFO", bg: "rgba(140,50,160,0.2)", color: "rgba(220,160,240,0.95)" }}
              collapsed={collapsed}
            />
          )}
          <NavItem
            to="/benchmark"
            label="Benchmark Pricing"
            Icon={Money24Regular}
            badge={{ text: "NEW", bg: "rgba(200,120,0,0.2)", color: "rgba(255,190,80,0.95)" }}
            collapsed={collapsed}
          />
          <NavItem
            to="/benchmark/intelligence"
            label="Market Intelligence"
            Icon={ArrowTrendingLines24Regular}
            highlight
            badge={{ text: "AI", bg: "rgba(200,120,0,0.22)", color: "rgba(255,200,100,0.95)" }}
            collapsed={collapsed}
          />

          {/* Data Entry — admin only */}
          {isAdmin && (
            <>
              <NavSection label="DATA ENTRY" collapsed={collapsed} />
              <NavItem to="/data-entry" label="New Entry" Icon={DocumentAdd24Regular} collapsed={collapsed} />
              <NavItem to="/excel-upload" label="Excel Upload" Icon={ArrowUpload24Regular} collapsed={collapsed} />
            </>
          )}

          {/* Admin / Config — manager and above */}
          {isManager && (
            <>
              <NavSection label="CONFIGURATION" collapsed={collapsed} />
              <NavItem
                to="/entities"
                label="Entity Manager"
                Icon={Organization24Regular}
                highlight
                badge={{ text: "ADMIN" }}
                collapsed={collapsed}
              />
            </>
          )}

          {/* Settings — editor and above */}
          {(isEditor || isAdmin) && (
            <>
              <NavSection label="SETTINGS" collapsed={collapsed} />
              <NavItem
                to="/admin/dept-owner-map"
                label="Dept. Owner Map"
                Icon={BuildingMultiple24Regular}
                highlight
                badge={{ text: "EDITOR" }}
                collapsed={collapsed}
              />
            </>
          )}

          {/* Admin — admin only */}
          {isAdmin && (
            <>
              <NavSection label="ADMIN" collapsed={collapsed} />
              <NavItem
                to="/admin/users"
                label="User Access"
                Icon={ShieldLock24Regular}
                highlight
                badge={{ text: "ADMIN" }}
                collapsed={collapsed}
              />
            </>
          )}
        </nav>

        {/* Collapse toggle */}
        <div style={{ padding: "12px 8px", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
          <Button
            appearance="subtle"
            icon={collapsed ? <ChevronRight24Regular /> : <ChevronLeft24Regular />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ width: "100%", color: "rgba(255,255,255,0.65)", justifyContent: "center" }}
          />
        </div>
      </aside>

      {/* Main content */}
      <div style={{ marginLeft: collapsed ? 64 : 240, flex: 1, display: "flex", flexDirection: "column", transition: "margin-left 0.2s ease", minHeight: "100vh" }}>
        <header style={{ height: 60, background: "#fff", borderBottom: "1px solid #EDEBE9", display: "flex", alignItems: "center", padding: "0 24px", gap: 16, position: "sticky", top: 0, zIndex: 50, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12 }}>
            <Text weight="semibold" size={400} style={{ color: "#201F1E" }}>Supply Chain Management</Text>
            {isDemoMode && <Badge appearance="tint" color="warning" style={{ fontSize: 11 }}>Demo Mode</Badge>}
          </div>
          <Menu>
            <MenuTrigger>
              <MenuButton appearance="subtle" icon={<Avatar name={user?.name || "User"} size={28} color="brand" />} style={{ gap: 8 }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                  <Text size={200} style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.name || "Guest User"}</Text>
                  {!accessLoading && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: roleBadgeColor, textTransform: "uppercase", letterSpacing: "0.4px" }}>{roleLabel}</span>
                  )}
                </div>
              </MenuButton>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<Person24Regular />} disabled>{user?.username || ""}</MenuItem>
                <MenuItem icon={<Settings24Regular />}>Settings</MenuItem>
                {isAdmin && (
                  <MenuItem icon={<ShieldLock24Regular />} onClick={() => window.location.href = "/admin/users"}>User Management</MenuItem>
                )}
                <MenuItem icon={<SignOut24Regular />} onClick={() => instance.logoutPopup({ postLogoutRedirectUri: "/" })}>Sign Out</MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </header>

        <main style={{ flex: 1, padding: "24px", overflowY: "auto" }}>
          <Outlet />
        </main>
        <TrainingNotification />
      </div>
    </div>
  );
}
